/*
 * Types for the JSON API, transcribed from docs/api.md (milestone 1).
 * ServerFacts comes from the shared protocol package.
 */
import type { MailRole, ServerMail, ServerMailSummary } from "./mail";
import type { ServerFacts } from "@hosterr/protocol";
import type { Coverage } from "../lib/uptime";
import type { RestartPlannedCard, ServerUpdates } from "./updates";
import type { AgentRow } from "./agentUpdates";

export type { ServerFacts };

export type OrgKind = "platform" | "agency" | "client";
/** `client` (milestone 6) is below developer: an agency's client sees its sites and mail, manages mailboxes, and asks for restores. */
export type Role = "owner" | "admin" | "developer" | "billing" | "viewer" | "client";

/** passkeys, google: whether they work at this address (not yet on an agency's own panel domain); absent from an older API. */
export type AuthState = { setupRequired: boolean; signedIn: boolean; mfaPending: boolean; passkeys?: boolean; google?: boolean };

export type MeOrg = {
  id: string;
  name: string;
  slug: string;
  kind: OrgKind;
  role: Role;
  /** Milestone 6: the agency a client org belongs to. */
  parentId?: string | null;
  /** Milestone 6, for owner and billing roles: an unpaid invoice to tell them about. */
  /** invoice: an emailed invoice past its due date (since), not a card payment that failed. */
  billingAlert?: { status: "past_due" | "unpaid"; since: string; amountDue: number; invoice?: boolean } | null;
  /** Billed on its own (an agency or a direct client), not as part of an agency's bill. Absent from an older API. */
  account?: boolean;
  /** Plans (docs/plans.md): sees and manages servers here, may connect one with the installer, shows the agency's brand. */
  servers?: boolean;
  connectServers?: boolean;
  ownBranding?: boolean;
  /** A client person's switches (Clients › What clients can do): sites, WordPress admin, mailboxes, DNS, restores. */
  client?: ClientAccess;
  /** Their org asks people at their domain to sign in with Google, and this session didn't (Sign-in domains). */
  googleRequired?: true;
};
/** What a client person can do (docs/access-permissions.md, "Clients"). */
export type ClientAccess = { sites: boolean; wpSignIn: boolean; email: boolean; dns: boolean; restores: boolean };
export type Me = {
  user: {
    id: string;
    name: string;
    email: string;
    totpEnabled: boolean;
    createdAt: string;
    /** This session signed in with a passkey, an authenticator code or a recovery code. Absent from an older API. */
    signedInWithTwoFactor?: boolean;
    /** How this session signed in; null: accepting an invite, or not known. Absent from an older API. */
    signedInWith?: "password" | "passkey" | "google" | null;
    /** With the authenticator app on; null without it. Absent from an older API. */
    recoveryCodesLeft?: number | null;
  };
  orgs: MeOrg[];
};

export type LoginResponse = { me: Me } | { mfaRequired: true };

/** issuer: the name the authenticator app shows (the person's brand). */
export type TotpSetup = { secret: string; otpauthUrl: string; qrSvg: string; issuer?: string };

/* ── Servers ────────────────────────────────────────────────────── */

export type ServerStatus = "provisioning" | "bootstrapping" | "ready" | "unreachable" | "failed" | "deleting";
export type Provider = "linode" | "byo";

export type LatestMetrics = {
  cpu: number;
  memUsed: number;
  memTotal: number;
  diskUsed: number;
  diskTotal: number;
  at: string;
};

export type ServerSummary = {
  id: string;
  name: string;
  provider: Provider;
  status: ServerStatus;
  statusDetail: string | null;
  region: string | null;
  plan: string | null;
  ipv4: string | null;
  agent: {
    connected: boolean;
    lastSeenAt: string | null;
    version: string | null;
    updateAvailable: boolean;
    /** What it can do beyond the base protocol, from its hello ("wp-sign-in", "webmail-master", "kexec", "service-restarts", "app-types"); [] for an older agent. */
    features?: string[];
  };
  metrics: LatestMetrics | null;
  environmentCount: number;
  /** Sites with an environment on it. */
  sites?: number | null;
  bootedAt?: string | null;
  /** Time since boot, from the agent's hello. */
  uptimeSeconds?: number | null;
  createdAt: string;
  /** Milestone 3: null until the store has been set up. ServerDetail carries the full card. */
  backupStore?: { status: BackupStoreStatus } | null;
  /** Milestone 4: from the last hourly audit; null values until there is one. ServerDetail carries the full picture. */
  security?: { rebootRequired: boolean | null; securityPackages: number | null } | null;
  /** Milestone 4: a restart the panel asked for. */
  reboot?: ServerReboot | null;
  /** Milestone 5: what the server does. */
  roles?: MailRole[];
  /** Milestone 5: null until mail is added to the server. ServerDetail carries the full card. */
  mail?: ServerMailSummary | null;
  /** Updates without downtime: a restart that's planned ("Restart planned"). ServerDetail carries the full `updates`. */
  restartPlanned?: RestartPlannedCard | null;
  /** Where its agent update stands, for the card's chip (docs/agent-updates.md); null when it's current. Absent: an older API. */
  agentUpdate?: Pick<AgentRow, "state" | "at" | "when" | "reason" | "updateReason"> | null;
  /** Planned downtime on it now (a restart, updates, planned work; docs/api.md, "Planned work"); null: none. Absent: an older API. */
  planned?: ServerPlanned | null;
};

/** What the panel has a server down for right now, on purpose (docs/api.md, ServerPlanned). */
export type ServerPlanned = {
  id: string;
  kind: PlannedKind;
  /** down: the work is under way (away for a restart); checking: done, its sites have a little while to answer. */
  phase: "down" | "checking";
  /** "Restarting", "Installing updates", "Planned work", "Checking sites". */
  label: string;
  /** What it's after, as a clause: "test-1 restarted", "the updates on test-1", "planned work on test-1". */
  reason: string;
  /** Planned work's note. */
  note: string | null;
  /** Who started it; null: nobody (the quiet window, the nightly run). */
  by: { id: string; name: string } | null;
  startedAt: string;
  backAt: string | null;
  /** Planned work's end. */
  until: string | null;
  operationId: string | null;
};
export type PlannedKind = "restart" | "refresh" | "upgrade" | "service" | "app" | "work";
/** Planned downtime covering an environment now: "Restarting", "Updating", "Planned work". */
export type EnvPlanned = { id: string; kind: PlannedKind; phase: "down" | "checking"; label: string; until: string | null; since: string };
/** Down time in a window, in seconds, by cause: the two add up to all of it. */
export type Downtime = { planned: number; unplanned: number };

/** An app type's worker and scheduler, as the last health check found them (docs/api.md, "App types"). */
export type AppHealth = {
  worker: { state: string; restarts: number; lastExit: string | null; since: string | null; lastLines: string[] } | null;
  scheduler: { lastRun: string | null; lastResult: string | null; lastLines: string[] } | null;
  /** What's wrong, in words: for the Status part and the site Overview's Needs a look. */
  problems: { kind: "worker" | "scheduler"; severity: "critical" | "warning"; title: string; detail: string; lines: string[] }[];
  checkedAt: string | null;
};

export type ServerReboot = {
  /** requested → rebooting (the agent said it's restarting) → done (it said hello again with a newer boot time), or failed. */
  status: "requested" | "rebooting" | "done" | "failed";
  operationId?: string;
  requestedAt: string;
  finishedAt: string | null;
  error: string | null;
};

export type EnvName = "live" | "test" | "dev";
/** An environment's role: the three in the Dev → Test → Live path, or an extra one (docs/extra-environments.md). */
export type EnvRole = EnvName | "extra";

export type ServerEnvironment = {
  id: string;
  siteId: string;
  siteName: string;
  name: EnvName;
  /** The name people see: "dev", "test", "live" or an extra environment's own; `label` is what to call it. */
  slug?: string;
  label?: string;
  primaryDomain: string;
  status: EnvStatus;
  /** Its certificate at a glance (Domains & HTTPS): where it comes from, its state and expiry. */
  certificate?: { source: "automatic" | "wildcard" | "custom"; status: "active" | "pending" | "failed" | "none"; validTo: string | null } | null;
};

export type ServerDetail = Omit<ServerSummary, "backupStore" | "security" | "mail"> & {
  /** What needs a look on it, worst first (api/attention.ts). Absent on older APIs. */
  attention?: import("./attention").AttentionItem[] | null;
  facts: ServerFacts | null;
  environments: ServerEnvironment[];
  /** Milestone 3: where this server's backups are kept. Null until it's set up. */
  backupStore?: BackupStore | null;
  /** Milestone 4: null until the first audit. */
  security?: ServerSecurity | null;
  /** Milestone 5: the Mail card. null while the server doesn't handle mail. */
  mail?: ServerMail | null;
  /** Updates without downtime (docs/rebootless-updates.md). Absent on older APIs. */
  updates?: ServerUpdates;
  /** The live mail domains on it, by name: removing it waits for them, like its sites. Absent on older APIs. */
  mailDomains?: string[];
  /** Its PHP versions, and adding one ("Add PHP 8.4"). Absent on older APIs. */
  php?: ServerPhp;
};

/** Every PHP version Hosterr offers (newest first) and whether the server has it; `reason` says why none can be added. */
export type ServerPhp = {
  versions: { version: PhpVersion; installed: boolean; running: string | null }[];
  canAdd: boolean;
  reason: string | null;
  installing: { version: string | null; operationId: string } | null;
};

export type MetricsRange = "1h" | "24h" | "7d";
export type MetricPoint = {
  at: string;
  cpu: number;
  memUsed: number;
  memTotal: number;
  diskUsed: number;
  diskTotal: number;
  load1: number | null;
};

export type CreateServer = (
  | { name: string; provider: "linode"; region: string; plan: string }
  | { name: string; provider: "byo" }
) & {
  /** Milestone 5: what it's for. Default ["web"]. "git": a code server for Hosterr repositories, alone (Hosterr's own). */
  roles?: (MailRole | "git")[];
};

export type CreateServerResponse = { server: ServerSummary; installCommand?: string };

export type LinodeMeta = {
  /** False when no Linode token is configured. */
  available: boolean;
  /** False when creating servers is switched off on this installation. */
  provisioningEnabled: boolean;
  regions: { id: string; label: string }[];
  /** group: shared CPU, then dedicated CPU (cores of its own); absent from an older API (shared). */
  plans: { id: string; label: string; group?: "shared" | "dedicated"; monthlyUsd: number; memoryMb: number; vcpus: number; diskGb: number }[];
};

/* ── Sites ──────────────────────────────────────────────────────── */

/** "php" sites are plain PHP apps (milestone 4 added them on the API side). */
export type SiteKind = "wordpress" | "static" | "php" | "node";
export type PhpVersion = "8.1" | "8.2" | "8.3" | "8.4" | "8.5";
export const PHP_VERSIONS: PhpVersion[] = ["8.5", "8.4", "8.3", "8.2", "8.1"];

export type EnvStatus = "creating" | "ready" | "failed" | "deleting";
export type SslStatus = "none" | "pending" | "active" | "failed";

export type AssetCache = "off" | "short" | "long";
export type PhpLimits = {
  memoryLimit: string;
  uploadMaxMb: number;
  maxExecutionSeconds: number;
  /* The rest of its PHP settings (docs/api.md, "PHP settings"); absent on an older API. */
  maxInputVars?: number;
  /** PHP-FPM's workers for its pool: in force, the server's default and most, and whether someone chose the number. */
  workers?: { count: number; default: number; max: number; custom: boolean };
  opcache?: boolean;
  displayErrors?: boolean;
  /** php.ini directives from the allowlist, normalized ("short_open_tag": "on"). */
  ini?: Record<string, string>;
  /** The extensions its version has on the server; null until the agent reports them. */
  extensions?: string[] | null;
  /** Every extension for its version, with its state here (docs/api.md, "PHP extensions"); null until reported. */
  extensionList?: PhpExtensionRow[] | null;
  /** The optional extensions it turns on. */
  turnedOn?: string[];
  /** Why the optional extensions can't be turned on or off yet (an older agent), or null. */
  extensionsUpdate?: string | null;
  /** The largest memory limit its server allows, in MB. */
  memoryMax?: number;
  /** PHP's support for its version; null for one Hosterr has no dates for. */
  support?: { status: "supported" | "security-only" | "eol"; activeUntil: string | null; securityUntil: string | null } | null;
  /** Why the settings past the limits can't change yet (an older agent), in the usual words; null when they can. */
  agentUpdate?: string | null;
};
/** One extension for an environment's PHP version: on for every site there (server), on or off here, or not installed. */
export type PhpExtensionRow = {
  name: string;
  group: "common" | "database" | "images" | "caching" | "other" | "builtin";
  about: string;
  state: "server" | "on" | "off" | "missing";
  /** Turned on here (also while it's missing, after a version switch). */
  wanted: boolean;
  /** On because another one needs it ("memcached"). */
  with: string | null;
};
/** PATCH …/environments/:envId `php`: any of them; `workers: null` is back to the server's default, `ini` and `extensions` the whole set (extensions alone). */
export type PhpPatch = Partial<Pick<PhpLimits, "memoryLimit" | "uploadMaxMb" | "maxExecutionSeconds" | "maxInputVars" | "opcache" | "displayErrors" | "ini">> & { workers?: number | null; extensions?: string[] };

export type EnvironmentSummary = {
  id: string;
  /** Sites list only (docs/api.md, "Card actions"): the person can sign in to this Live's WordPress from the card. */
  canWpSignIn?: boolean;
  /** Sites list only: commits waiting for Live (as `pipeline.live.ahead`); null without a repository or before the comparison. */
  toDeploy?: number | null;
  /** The role. Lookups by name work for Dev, Test and Live; an extra environment is "extra", told apart by its slug. */
  name: EnvRole;
  /** The name people see and the address uses: "dev", "test", "live", or an extra environment's own ("customers"). Absent on older APIs. */
  slug?: string;
  kind?: "pipeline" | "extra";
  /** "Dev", "Test", "Live" or the slug: what to call it on screen. */
  label?: string;
  /** An extra environment on a git site: the branch it follows. Null for Dev, Test and Live. */
  branch?: string | null;
  /** An extra environment: a push to its branch deploys it. */
  deployOnPush?: boolean;
  /** WordPress Dev, Test and extra environments: a missing upload loads from Live's address. Null for Live and other kinds. */
  uploadsFromLive?: boolean | null;
  /** An extra environment on a git site: what's waiting for it, and its branch against the site's. */
  branchDiff?: BranchDiff | null;
  /** Nightly backups (the same as backups.enabled); off for a new extra environment. */
  nightlyBackups?: boolean;
  /** WordPress: its persistent object cache in the server's Valkey. Null for static and PHP sites. */
  objectCache?: ObjectCache | null;
  /** App types: the queue worker and the scheduler, from the last health check (null for other sites, or before an agent reports it). */
  app?: AppHealth | null;
  /** Live, for 24 hours after a copy into it: undoing it restores the restore point taken first. */
  copyUndo?: { backupId: string; operationId: string; label: string; until: string; parts: { db: boolean; files: boolean } } | null;
  /** Addresses added to it that are waiting for DNS, or stopped waiting: for the site's Overview. */
  addressesWaiting?: { hostname: string; state: "waiting" | "gave_up"; detail: string | null }[];
  /** Its open move to another server, in a few words (api/moves.ts has the move itself); null: none. */
  move?: { id: string; state: string; tone: "info" | "warn" | "bad"; words: string } | null;
  status: EnvStatus;
  statusDetail: string | null;
  /** Null, with the name, for people who don't see that server. */
  serverId: string | null;
  /** Null for people who don't see servers (docs/plans.md); `location` says where instead. */
  serverName: string | null;
  /** The server's city ("Atlanta, GA"); null for a server run elsewhere. */
  location?: string | null;
  primaryDomain: string;
  url: string;
  domains: { hostname: string; primary: boolean }[];
  phpVersion: string;
  /**
   * The PHP versions its server has, whether it can add another (installed first), and which it can run at all
   * (`offered`: an older agent doesn't know 8.5; absent from an older API). Null for static and Node.js sites.
   */
  phpOnServer?: { installed: string[]; adds: boolean; offered?: string[] } | null;
  /** WordPress's scheduled tasks from the server, how often; null while WordPress runs them on visits. */
  wordpressCron?: { everyMinutes: number } | null;
  /** The server limits outbound connections to an allowlist (a mail provider's host has to be on it). */
  outboundAllowlist?: boolean;
  ssl: { status: SslStatus; expiresAt: string | null; detail?: string | null };
  /** `detail`: why the last check failed, in plain words; `detailRaw`: the checker's own text, for the log only. */
  health: { httpStatus: number | null; ms: number | null; checkedAt: string | null; detail?: string | null; detailRaw?: string | null };
  wp: { version: string | null } | null;
  /* Milestone 2 */
  code: {
    layout: CodeLayout;
    target: DeployTarget | null;
    /** Name of the release running now, once deployed from git. */
    release: string | null;
    /** `author` is git's "Name <email>"; see authorName(). `by`: who deployed what it runs now. */
    deployed: {
      sha: string;
      message: string | null;
      subject?: string | null;
      /** The rest of the commit message, after the subject. */
      body?: string | null;
      /** That deploy's note, and where it came from. */
      note?: string | null;
      source?: DeploySource | null;
      author: string | null;
      at: string | null;
      by?: { kind: "person" | "webhook" | "setup"; id: string | null; name: string } | null;
    } | null;
  };
  /** HTTP basic auth in front of the environment (Live too, on all its addresses). */
  locked: { user: string } | null;
  /** Maintenance mode: since when, who turned it on, how many addresses get past it; null when off (absent on older APIs). */
  maintenance?: { since: string; by: string | null; addresses: number } | null;
  /** Website feedback (docs/feedback.md): on, who gets the button, its comments and when they last moved; null on Live. Absent: an older API. */
  feedback?: { on: boolean; audience: "everyone" | "links"; open: number; total: number; lastAt: string | null; agentUpdate: boolean } | null;
  /** Which of the site's servers it's on, without naming it (0: Live's). Copies and restores need the same one. */
  serverGroup?: number;
  /** The caller's level on this environment (team access). Environments at "none" aren't sent at all. */
  access?: import("./access").Level;
  /** Uptime per window, in percent with two decimals (99.96), rounded down; null under 12 checks in the window. */
  uptime?: { day: number | null; week: number | null; month: number | null } | null;
  /** How much of each uptime window the checks cover (lib/uptime.ts). */
  uptimeCoverage?: { day: Coverage; week: Coverage; month: Coverage } | null;
  /** Each window's down time by cause, in seconds, and uptime outside planned work (the planned time left out). Absent: an older API. */
  downtime?: { day: Downtime; week: Downtime; month: Downtime } | null;
  uptimeOutsidePlanned?: { day: number | null; week: number | null; month: number | null } | null;
  /** Planned downtime covering it now (a restart, planned work on its server); null: none. Absent: an older API. */
  planned?: EnvPlanned | null;
  /** Response time over the last 24 hours, in ms. */
  responseMs?: { p50: number | null; p95: number | null } | null;
  /** The last 24 hours: hourly median response in ms, oldest first; null for an hour without an answer. */
  spark?: (number | null)[] | null;
  noindex: boolean;
  /** WordPress's page cache. Absent until the API reports it. */
  pageCache?: boolean;
  /**
   * WordPress: pages whose only query parameters are ad and analytics ones (gclid, fbclid, msclkid, utm_*) are cached,
   * and PHP doesn't see those parameters. agentUpdate: why the server can't yet. Null for other kinds; absent from an
   * older API.
   */
  pageCacheAdClicks?: { on: boolean; agentUpdate: string | null } | null;
  /** WordPress's login page's own address the server has in force ("team-login"); null: wp-login.php. */
  wpLoginPath?: string | null;
  /** How long browsers keep CSS, JS, images and fonts (HTML is never cached). Absent until the API reports it. */
  assetCache?: AssetCache;
  /** PHP's limits (PHP sites); absent until the API reports them. */
  php?: PhpLimits | null;
  /** Still being created: apply → deploy (if Live uses git) → copy from Live. */
  settingUp: boolean;
  /** Milestone 3. Optional until every API response carries it. */
  backups?: EnvBackupSummary;
  /** Milestone 4: what the card's quiet line needs. */
  security?: SiteSecuritySummary;
};

export type SiteSummary = {
  id: string;
  name: string;
  kind: SiteKind;
  /** A php site's preset (Laravel, Drupal…); null for WordPress and Static, and absent on older APIs. */
  appType?: string | null;
  createdAt: string;
  live: EnvironmentSummary | null;
  /** Set on live events once a site has been deleted. */
  deleted?: boolean;
  /** Milestone 3: take a restore point of Live's database before each deploy to Live (WordPress). */
  restorePointBeforeLiveDeploy?: boolean;
  /** Milestone 3: backup problems in every environment of the site. */
  backupAlerts?: BackupAlert[];
  /** Milestone 4: across every environment of the site. */
  security?: SiteSecuritySummary;
  /** A screenshot of Live's homepage, once the capture job has taken one. Null (or absent) until then. */
  thumbnail?: SiteThumbnail | null;
  /** Live's uptime over 7 days, percent (99.96); null under 12 checks. */
  uptime?: number | null;
  /** How much of those 7 days the checks cover. */
  uptimeCoverage?: Coverage | null;
  /** Live's last 24 hours: hourly median response in ms, oldest first. */
  spark?: (number | null)[] | null;
  /** Dev → Test → Live for the sites list's strip; null where the site has no such environment. Absent on older APIs. */
  pipeline?: SitePipeline;
  /**
   * The caller's site abilities (team access), whether they may delete the site, and whether they can deploy to every
   * environment (`deployAll`), which changing the app's settings or the repository takes. deployAll: absent on older APIs.
   */
  access?: { abilities: import("./access").Abilities; deleteSite: boolean; deployAll?: boolean };
  /** Extra environments: how many the site has and may have. Absent on older APIs. */
  extraEnvironments?: ExtraEnvironmentCount;
  /** The team's own labels for the site ("retainer", "shop"), as typed. Absent on older APIs. */
  tags?: string[] | null;
  /** Each of its tags' colors (a preset key or #rrggbb), from its org's tag list. */
  tagColors?: Record<string, string> | null;
  /** What needs a look on it, worst first (api/attention.ts). Absent on older APIs. */
  attention?: import("./attention").AttentionItem[] | null;
  /** The agency client the site belongs to; null for the organization's own. Absent on older APIs. */
  client?: SiteClient | null;
};

/** The client org a site is in (an agency's client, or one of the platform's direct clients); its pages are under its slug. */
export type SiteClient = { id: string; name: string; slug: string };

/** docs/object-cache.md. `status` is what the last apply reported (null before one); `stats` an hourly, approximate sample. */
export type ObjectCache = {
  enabled: boolean;
  status: "on" | "off" | "unavailable" | "foreign" | null;
  detail: string | null;
  stats: { hitRate: number | null; hits: number; misses: number; keys: number; bytes: number; at: string } | null;
};

/** An extra environment's branch: commits waiting to deploy to it, and the branch against the site's branch. */
export type BranchDiff = {
  branch: string;
  waiting: PipelineHop | null;
  compare: { base: string; ahead: number; behind: number; url: string | null } | null;
};

/** How many extra environments a site has and may have. `used` counts ones the caller can't see too. */
export type ExtraEnvironmentCount = { used: number; limit: number; included: number; cap: number };

export type NewEnvironment = {
  name: string;
  branch?: string;
  codeFrom?: "dev" | "live";
  contentFrom?: string;
  copyUploads?: boolean;
  deployOnPush?: boolean;
  dryRun?: boolean;
  id?: string;
};

export type NewEnvironmentPreview = {
  dryRun: true;
  id: string;
  hostname: string;
  /** Null for people who don't see servers (docs/plans.md). */
  server: { id: string; name: string } | null;
  summary: string;
  estimateBytes: number | null;
  freeBytes: number | null;
  disk: { level: "ok" | "warn" | "refuse"; usedPercent: number | null; afterPercent: number | null; message: string | null };
  extraEnvironments: ExtraEnvironmentCount | null;
};

export type PipelineStage = {
  exists: true;
  environmentId: string;
  status: EnvStatus;
  health: { ok: boolean | null; httpStatus: number | null; checkedAt: string | null };
  /** Commits waiting to be deployed to this stage ("2 to deploy"); null when not known yet. */
  ahead?: number | null;
  /** What it runs. */
  sha?: string | null;
};
export type SitePipeline = { dev: PipelineStage | null; test: PipelineStage | null; live: PipelineStage | null };

export type SiteThumbnail = {
  url: string; // 640×400
  smallUrl?: string; // 320×200
  takenAt: string;
  httpStatus?: number | null;
  lastAttempt?: { at: string; error: string } | null;
  /** A new picture has been asked for, or is being taken now. */
  refreshing?: boolean;
};

/** One commit between two deployed points. */
export type PipelineCommit = { sha: string; subject: string; body?: string | null; author: string | null; authoredAt: string | null; url?: string | null };
/**
 * One step of the pipeline (the branch to the first environment, or one environment to the next):
 * what the target runs (`from`), the source (`to`: a branch or the previous environment's sha),
 * commits waiting for the target (`ahead`, newest first in `commits`, up to 50) and commits only the target has (`behind`).
 */
export type PipelineHop = { from: string | null; to: string; toSha: string | null; ahead: number; behind: number; commits: PipelineCommit[]; truncated: boolean };
/** Keys: branchToDev, devToTest, testToLive (devToLive without Test, branchToLive with only Live). A null step isn't known yet. */
export type PipelineDiff = Partial<Record<string, PipelineHop | null>>;

export type SiteDetail = SiteSummary & {
  environments: EnvironmentSummary[];
  /** webUrl: the repository's page; commitUrl: a link template with {sha}. Both absent on older APIs. */
  /** firstDeployWaits: a clone of a git site, until its deploy key works and its first deploy runs. Absent on older APIs. */
  repo: { url: string; branch: string; source?: "connected" | "hosted"; webUrl?: string | null; commitUrl?: string | null; firstDeployWaits?: boolean } | null;
  /** Null without a repository. */
  pipelineDiff?: PipelineDiff | null;
  pipelineDiffCheckedAt?: string | null;
  /** A check is on its way. */
  pipelineDiffRefreshing?: boolean;
  /** Why the last check failed; the last good answer stays. */
  pipelineDiffError?: string | null;
  /** For people who can delete the site (else null); absent on older APIs. */
  deletion?: SiteDeletion | null;
  /** A static site's single-page-app fallback: an address that isn't a file gets index.html. Null for other kinds. */
  spaFallback?: boolean | null;
};

/** Deleting a site (docs/api.md, "Restore points before risky work"). */
export type SiteDeletion = {
  /** The environments (slugs) a delete backs up first, Live first: those with something on their server. */
  finalBackups: string[];
  /** The last delete a failed final backup stopped (for 3 days), until it's continued or a delete runs again. */
  stopped: { at: string; title: string; reason: string | null } | null;
};

export type CreateSite = {
  name: string;
  kind: SiteKind;
  /** Required with kind "php": the preset (docs/api.md, "App types"). */
  appType?: "laravel" | "statamic" | "drupal" | "craft" | "symfony" | "php";
  /** A PHP app's web root (default "public"); the other types set their own. */
  docroot?: string;
  /** Left out: Hosterr places it (docs/plans.md); only people who see servers choose one. */
  serverId?: string;
  domain?: string;
  /** Live's preview address, `<label>.<zone>`, instead of the automatic one (docs/api.md, "Choosing a preview address"). */
  previewLabel?: string;
  phpVersion: PhpVersion;
  wordpress?: { title: string; adminEmail: string };
};

export type WpAdmin = { url: string; user: string; password: string };

export type CreateSiteResponse = { site: SiteSummary; operationId: string; wpAdmin?: WpAdmin };

/* ── Operations and activity ────────────────────────────────────── */

export type OperationStatus = "queued" | "sent" | "running" | "succeeded" | "failed" | "cancelled" | "expired";

export type Actor = { id: string; name: string };

export type Operation = {
  id: string;
  kind: string;
  title: string;
  status: OperationStatus;
  serverId: string;
  environmentId: string | null;
  siteId: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  error: { code: string; message: string } | null;
  changed: string[];
  /** Allow-listed per kind and set once it succeeded: `site.cache.flush` gives `{ keysDeleted }`; null otherwise. */
  result?: { keysDeleted?: number } | null;
  actor: Actor | null;
  /** Set on restore points: the work waiting on them. */
  followUp?: OperationFollowUp | null;
  /** While it runs: how long ones like it took on this server (the median), or null without history. */
  estimateSeconds?: number | null;
  /** A step of a server move (api/moves.ts): the task tray shows the move as one task instead. */
  moveId?: string | null;
  /** Health checks, audits, inventories, facts, pipeline lookups. */
  routine?: boolean;
  /** A routine job the server's agent was too old to know: not a failure, and not shown. */
  quiet?: boolean;
  /**
   * What came of it, in words for people, when the API has them (a health check: "kn-beta.preview.page is online",
   * "It answered in 182 ms."); null when it didn't run, and for kinds without one yet.
   */
  outcome?: OperationOutcome | null;
};

export type OperationOutcome = { tone: "good" | "bad"; title: string; detail: string | null };

export type OperationFollowUp = {
  /** "Deploy 0d1a969 to Live", "Delete the Dev environment", "Delete the site Acme" */
  label: string;
  status: "waiting" | "dispatched" | "cancelled";
  operationId: string | null;
  error: string | null;
  /** What the work is: "deploy", "update", "clone", "restore", "remediate", "remove_environment", "remove_site". */
  type?: string | null;
};

export type LogStream = "stdout" | "stderr" | "info";
export type LogLine = { seq: number; at: string; stream: LogStream; line: string };

/**
 * `technical`: whether this person reads the log and the technical details (owners, admins, developers, and deploy
 * on its environment). Without it, `logs` is empty and the operation's `changed` too: the sheet shows the answer,
 * who and when, and the error in words. Absent from an older API: true.
 */
export type OperationWithLogs = { operation: Operation; logs: LogLine[]; technical?: boolean };

export type ActivityEvent = {
  id: string;
  at: string;
  actor: Actor | null;
  action: string;
  summary: string;
  target: { type: string; id: string; name: string } | null;
};

/* ── Organization ───────────────────────────────────────────────── */

export type Via = "member" | "agency";

export type OrgInfo = {
  org: { id: string; name: string; slug: string; kind: OrgKind; requireMfa: boolean; createdAt: string; /** IANA name, e.g. America/New_York. */ timezone?: string };
  role: Role;
  via: Via;
};

export type Member = {
  userId: string;
  name: string;
  email: string;
  role: Role;
  via: Via;
  /** Team access: every site, or only some (docs/api.md, "Team access"). */
  siteScope?: "all" | "sites";
  /** Sites given them by name: with Only these sites, the ones they have; with All sites, the ones changed from the role's default. */
  ownSites?: number;
  /** The groups they're in (for admins). */
  groups?: { id: string; name: string }[];
  /** A client person's switches. */
  client?: ClientAccess;
  totpEnabled: boolean;
  /** Passkeys on their account (they count as two-factor when someone signs in with one). Absent from an older API. */
  passkeys?: number;
  lastLoginAt: string | null;
  joinedAt: string;
};

/* ── Milestone 2: environments, git, deploys, passkeys ─────────────
 * docs/api.md, "Environments and code" and "Passkeys". */

export type CodeLayout = "plain" | "releases";
export type DeployTarget = "wp-content" | "docroot";

export type Deployment = {
  id: string;
  environmentId: string;
  environment: EnvName;
  kind: "deploy" | "rollback";
  trigger: "manual" | "webhook" | "setup" | "push"; // push: a push to the site's Hosterr repository
  status: "pending" | "succeeded" | "failed";
  ref: string | null;
  sha: string | null;
  message: string | null;
  /** The commit message's first line. */
  subject?: string | null;
  /** The rest of the commit message. */
  body?: string | null;
  /** The commit in the repository, when it's on GitHub, GitLab or Bitbucket. */
  commitUrl?: string | null;
  /** What the person wrote about this deploy. */
  note?: string | null;
  /** Where it came from: the branch, another ref, a push, Dev or Test, a rollback, or setup. */
  source?: DeploySource | null;
  author: string | null;
  committedAt: string | null;
  release: string | null;
  /** True for the release the environment runs now. */
  current: boolean;
  /** The release is still on the server, so it can be rolled back to. */
  available: boolean;
  promotedFrom: "dev" | "test" | null;
  error: string | null;
  operationId: string | null;
  actor: Actor | null;
  createdAt: string;
  finishedAt: string | null;
};

export type SiteRepo = {
  url: string;
  branch: string;
  subdir: string;
  deployTarget: DeployTarget;
  build: string | null;
  shared: string[];
  /** Public key: add it to the repository as a read-only deploy key. */
  deployKey: string;
  /** The secret is "" for roles below admin. */
  webhook: { url: string; secret: string };
  autoDeployDev: boolean;
  /** `branches` comes from the last successful check. */
  lastCheck: { at: string; ok: boolean; detail: string; branches: { name: string; sha: string }[] } | null;
  lastWebhook: { at: string; sha: string | null } | null;
  /** From the last check: what composer.json or package.json suggest ("laravel"; a static site's framework). */
  detected?: { appType: string | null; framework: string | null; at: string } | null;
  /** A JavaScript build's Node.js major ("22"); null is Automatic. Absent on an API from before it. */
  nodeVersion?: string | null;
  /** The versions on offer and what the last build ran with (pages/sites/NodeVersion.tsx). */
  node?: NodeBuild;
  /** A static site's served folder ("dist", "." the top); null is Automatic. Absent on an API from before it. */
  publishDir?: string | null;
  /** The folder the last build served (pages/sites/PublishDir.tsx). */
  publish?: PublishInfo;
  /** Connected to another host, or hosted by Hosterr (docs/hosted-repos.md). Absent on an API from before it: connected. */
  source?: "connected" | "hosted";
  /** A Hosterr repository: how to clone and push, and what you can do. Null for a connected one. */
  hosted?: HostedRepo | null;
  /**
   * A clone of a site deployed from git: Live's first deploy (the source Live's commit; for WordPress its database and
   * uploads after it), waiting for this key to be checked. Null otherwise; absent on older APIs.
   */
  firstDeploy?: { ref: string; from: string; database: boolean; uploads: boolean } | null;
};

/** SiteRepo.hosted (docs/api.md, "Hosterr repositories"). */
export type HostedRepo = {
  name: string; // bakery-3f9a1c
  cloneUrl: string; // git@git.hosterr.com:bakery-3f9a1c.git
  cloneCommand: string;
  host: string;
  port: number;
  hostKeys: { type: string; fingerprint: string }[];
  state: "creating" | "ready" | "failed";
  detail: string | null;
  bytes: number | null;
  limits: { fileBytes: number; pushBytes: number; repoBytes: number; refs: number };
  lastPush: { at: string; by: { id: string; name: string } | null; updates: { ref: string; old: string; new: string; forced: boolean; commits: number }[] } | null;
  /** Your keys, and what you may do: clone, push main, push the followed branches named, push other branches and tags. */
  you: { keys: number; read: boolean; main: boolean; branches: string[]; other: boolean };
};

/** GET …/repo's `hosting`: whether a site without a repository can start a Hosterr one here. */
export type RepoHosting = { available: boolean; reason: string | null };

/** SiteRepo.publish: the setting and the folder the last successful build served. */
export type PublishInfo = {
  dir: string | null;
  last: { dir: string; why: string; at: string; environment: string } | null; // dir "" is the top
  needsUpdate: string[];
};

/** SiteRepo.node: the Node.js setting's choices, and the last successful build's pick and why. */
export type NodeBuild = {
  version: string | null;
  majors: { major: string; lts: boolean }[];
  last: {
    version: string; // "22.21.0"
    source: string; // "the site's setting", ".nvmrc", ".node-version", "package.json engines.node"; "" for none (the newest LTS)
    spec: string; // what it said: "22", ">=20"
    packageManager: string; // "pnpm 9.12.0"
    packageManagerSource: string;
    installed: boolean;
    at: string;
    environment: string; // "Live"
  } | null;
  /** Servers of the site whose agent needs an update for this. */
  needsUpdate: string[];
};

/** First connect needs url and branch; after that, only the fields sent change. */
export type ConnectRepo = {
  url?: string;
  branch?: string;
  subdir?: string;
  deployTarget?: DeployTarget;
  build?: string | null;
  nodeVersion?: string | null;
  publishDir?: string | null;
  autoDeployDev?: boolean;
};

export type Passkey = {
  id: string;
  name: string;
  /** Saved to a password manager or cloud keychain, so it can be on several devices. */
  synced: boolean;
  createdAt: string;
  lastUsedAt: string | null;
};

/* ── Milestone 3: backups (docs/milestone-3.md, "HTTP API") ──────── */

export type BackupKind = "scheduled" | "manual" | "restore_point" | "final";
export type BackupStatus = "pending" | "running" | "succeeded" | "failed" | "missed";
export type BackupParts = { db: boolean; files: boolean };

export type BackupPolicy = {
  enabled: boolean;
  /** Hour of day in UTC; the minute is fixed per environment (see nextAt). */
  hourUtc: number;
  keepDaily: number;
  keepWeekly: number;
  keepMonthly: number;
};

/** The weekly test restore, as the agent reports it (protocol BackupVerifyResult). */
export type BackupVerifyResult = {
  files: { expected: number; restored: number; bytes: number } | null;
  db: { tables: number; bytes: number; wordpressOk: boolean | null } | null;
  check: { ok: boolean; readDataSubset: string };
  partial: string | null;
  durationMs: number;
};

export type DownloadStatus = "pending" | "running" | "ready" | "failed" | "expired" | "deleted";

export type Backup = {
  id: string;
  environmentId: string;
  envName: EnvRole;
  /** True for the final backup of an environment that has since been deleted. */
  environmentDeleted: boolean;
  /** Which of the site's servers it was taken on, numbered as the environments' serverGroup; -1: none of theirs now. */
  serverGroup?: number;
  kind: BackupKind;
  /** The human label: "Nightly", "Manual", "Before deploying 0d1a969 to Live", "Before deleting the site". */
  reason: string;
  note: string | null;
  parts: BackupParts;
  status: BackupStatus;
  /** When it was taken (for "missed": the slot it should have run in). */
  createdAt: string;
  finishedAt: string | null;
  expiresAt: string | null;
  release: string | null;
  sha: string | null;
  filesCount: number | null;
  bytesTotal: number | null;
  bytesAdded: number | null;
  dbBytes: number | null;
  durationMs: number | null;
  /** `result` is null when the test restore couldn't run; `operationId` opens its sheet (the answer, in words). */
  verified: { at: string; ok: boolean; summary: string | null; result: BackupVerifyResult | null; operationId?: string | null } | null;
  /** On restore points: the work it protects ("Deploy 0d1a969 to Live"). */
  followUp: { status: "waiting" | "dispatched" | "cancelled"; label: string } | null;
  error: string | null;
  actor: Actor | null;
  operationId: string | null;
  /** Its unexpired downloads. */
  downloads: { id: string; part: "db" | "files"; status: DownloadStatus; size: number | null; expiresAt: string }[];
};

export type EnvBackupState = {
  policy: BackupPolicy;
  /** The backup policy it follows (docs/backup-policies.md); absent from an older API. */
  backupPolicy?: import("./backupPolicies").EnvBackupPolicy;
  /** Hosterr's admins set it. */
  canSetPolicy?: boolean;
  /** The next nightly slot; null when nightly backups are off. */
  nextAt: string | null;
  lastSucceededAt: string | null;
  lastVerify: { at: string; ok: boolean; summary: string | null } | null;
  /** The weekly test restore. In the past while one is due but hasn't run; null with no backup to test. */
  nextVerifyAt: string | null;
};

export type BackupsResponse = {
  backups: Backup[];
  environments: Record<string, EnvBackupState>;
};

/** What each environment's JSON carries about its backups. */
export type EnvBackupSummary = {
  lastSucceededAt: string | null;
  nextAt: string | null;
  lastVerify: { at: string; ok: boolean } | null;
  enabled: boolean;
};

export type BackupAlert = {
  envName: EnvName;
  kind: "stale" | "verify_failed" | "restore_point_failed";
  since: string;
  /** A sentence, e.g. "Live hasn't been backed up in 2 days." */
  detail: string;
};

export type BackupDownload = {
  status: DownloadStatus;
  part: "db" | "files";
  filename: string;
  size: number | null;
  sha256: string | null;
  expiresAt: string | null;
  /** A link that works for 15 minutes, minted on each request while the download is ready. */
  url: string | null;
  error: string | null;
};

export type BackupStoreStatus = "creating" | "ready" | "failed";

export type BackupStore = {
  status: BackupStoreStatus;
  /** Also says what hardening didn't take. */
  statusDetail: string | null;
  region: string;
  regionLabel: string;
  bucket: string;
  endpointType: string | null;
  /** Only what the bucket confirmed on read-back. */
  versioning: boolean;
  lifecycle: boolean;
  rawBytes: number | null;
  snapshotCount: number | null;
  lastPruneAt: string | null;
  lastCheckAt: string | null;
  lastError: { at: string; message: string } | null;
};

/** Routes that take a restore point before risky work answer this. */
export type RestorePointRef = { backupId: string; operationId: string } | null;
export type DeploySource = "branch" | "ref" | "push" | "dev" | "test" | "rollback" | "setup";

export type ChainedResponse = { operationId: string; restorePoint: RestorePointRef };

export const ACTIVE_OP_STATUSES: OperationStatus[] = ["queued", "sent", "running"];
export const isActiveOp = (s: OperationStatus) => ACTIVE_OP_STATUSES.includes(s);

/* ── Security (milestone 4) ─────────────────────────────────────── */

export type Severity = "critical" | "high" | "medium" | "low" | "unknown";
export type ComponentType = "core" | "plugin" | "theme" | "php";
export type WafMode = "off" | "detect" | "block";
export type AutoUpdates = "off" | "security" | "all";

export type SecuritySettings = {
  xmlrpc: boolean;
  fileEditing: boolean;
  loginRateLimit: boolean;
  waf: WafMode;
  autoUpdates: AutoUpdates;
  /** Extra paths checked after each update, besides the home page and /wp-login.php. Up to 3. */
  healthPaths: string[];
};

export type InventoryItem = {
  slug: string;
  name: string;
  version: string | null;
  /** plugins: active | inactive | active-network | must-use | dropin; themes: active | parent | inactive */
  status: string;
  update: { version: string } | null;
  managedBy: "server" | "git";
  /** Open vulnerabilities in the installed version. */
  vulnerable: number;
  updateKind: "patch" | "minor" | "major" | null;
  /** Closed on WordPress.org, and why. */
  closed?: { reason: string | null } | null;
  /** Its picture, author and a line about it (docs/api.md, "Plugin and theme cards"); absent from an older API. */
  card?: ComponentCard | null;
};

/** What a plugin or theme looks like: image is a plugin's icon or a theme's screenshot; null draws its initials (ui/ComponentMark). */
export type ComponentCard = { author: string | null; description: string | null; image: string | null };

export type Inventory = {
  collectedAt: string;
  wordpress: {
    version: string;
    dbVersion: string | null;
    multisite: boolean;
    /** The newest on offer. */
    update: { version: string; kind: "minor" | "major" } | null;
    /** The newest on the installed branch, when the newest on offer is a major. */
    minorUpdate?: { version: string } | null;
    vulnerable?: number;
    /** WordPress's terms: "major" is a new x.y, "patch" a release on the branch. */
    updateKind?: "patch" | "minor" | "major" | null;
  } | null;
  php: { version: string; vulnerable: number } | null;
  plugins: InventoryItem[];
  themes: InventoryItem[];
};

export type VulnStatus = "open" | "ignored" | "fixed";

export type Vulnerability = {
  id: string;
  component: {
    type: ComponentType;
    slug: string;
    name: string;
    version: string;
    /** Closed on WordPress.org, and why, when the source says so. */
    closed?: boolean;
    closedReason?: string | null;
  };
  title: string;
  description?: string | null;
  severity: Severity;
  cvss: number | null;
  cves?: string[];
  fixedIn: string | null;
  unfixed: boolean;
  references: { label: string; url: string }[];
  status: VulnStatus;
  firstSeen: string;
  lastSeen?: string | null;
  fixedAt?: string | null;
  /** An inactive plugin: "inactive, but its files can still be reached". */
  inactive?: boolean;
  /** "hosterr": PHP end of life, written by Hosterr rather than a database. */
  source?: "wpvulnerability" | "hosterr";
  ignored?: { by: Actor | null; reason: string; version: string; at?: string | null } | null;
};

export type ScanFindingKind =
  | "core_modified"
  | "core_missing"
  | "core_unknown_file"
  | "plugin_modified"
  | "php_in_uploads"
  | "signature"
  | "user_ini"
  /** A .js file that matches the script rules (agents with "scan-javascript"). */
  | "js_signature"
  /** WordPress's database (agents with "scan-database"); path "database/options/<name>", "database/posts/<id>", "database/users/<id>". */
  | "db_address"
  | "db_script"
  | "db_encoded"
  | "db_admin";
export type ScanFindingStatus = "open" | "resolved" | "allowed" | "quarantined";
export type FindingAction = "replace" | "quarantine" | "allow" | "unquarantine";

export type ScanFinding = {
  id: string;
  kind: ScanFindingKind;
  /** The API's plain title: "A WordPress core file is missing". */
  label?: string;
  /** Relative to the environment's home. */
  path: string;
  sha256: string | null;
  size: number | null;
  modifiedAt: string | null;
  /** The signature's rule name, or the component ("plugin:akismet@5.3"). */
  rule: string | null;
  detail: string;
  severity: Exclude<Severity, "unknown">;
  status: ScanFindingStatus;
  firstSeen: string;
  lastSeen?: string | null;
  /** Code from the repository can't be fixed on the server. */
  gitManaged?: boolean;
  /** What can be done with it now. */
  actions?: FindingAction[];
  allowed?: { by: Actor | null; at: string | null } | null;
  quarantineId?: string | null;
  /** Its group's key (ScanGroup). Absent from an older API. */
  group?: string;
  /** Open again: the file changed after this person reviewed it. */
  changedSinceReview?: { by: Actor | null; at: string | null } | null;
  /** A database finding: its screen in wp-admin ("post.php?post=12&action=edit"), opened through one-click sign-in. */
  openPath?: string | null;
  /** Open: worth a look, or likely its plugin's or theme's own code, with why (docs/api.md, "Likely its own code"). Absent: an older API (worth a look). */
  trust?: FindingTrust | null;
};

/** Why a file is likely its plugin's or theme's own code. */
export type TrustReason =
  | { kind: "hosterr_reviewed"; at: string | null }
  | { kind: "same_elsewhere"; sites: number; accounts: number; servers: number }
  | { kind: "with_component"; at: string }
  | { kind: "pattern_only"; rules: string[] };
export type FindingTrust = {
  bucket: "look" | "own";
  reasons: TrustReason[];
  /** Written on its own while the rest of the component shares a time: when, and when the rest was. */
  ownTime: { at: string; usual: string } | null;
};

export type ScanStats = { files: number; bytes: number; durationMs: number };
/** database and javascript: agents with "scan-database" and "scan-javascript"; quick: a daily look. */
export type ScanChecks = { integrity: boolean; uploads: boolean; signatures: boolean; userIni: boolean; database?: boolean; javascript?: boolean; quick?: boolean };

/** Findings by where the files come from (docs/api.md, "Malware findings by component"). */
export type ScanGroup = {
  /** "core", "plugin:<slug>", "theme:<slug>", "mu-plugins", "uploads", "database", "other". */
  key: string;
  type: "core" | "plugin" | "theme" | "mu-plugins" | "uploads" | "database" | "other";
  slug: string | null;
  /** "WordPress", "Gravity Forms", "Whitehardt", "Must-use plugins", "Uploads", "Other files" ("Site files" off WordPress). */
  name: string;
  version: string | null;
  managedBy: "server" | "git" | null;
  /** official: compared with WordPress.org's copy; patterns: no official copy, so pattern rules only; uploads: any PHP is flagged; database: WordPress's database. */
  check: "official" | "patterns" | "uploads" | "database";
  why: "no_checksums" | "not_installed" | "plugins_unchecked" | "core_unchecked" | "not_checked" | "theme" | "mu_plugin" | "not_wordpress" | "own_code" | null;
  open: number;
  /** Of the open ones: worth a look, and likely the component's own code. Absent: an older API (all worth a look). */
  look?: number;
  ownCode?: number;
  reviewed: number;
  quarantined: number;
  /** Open, and changed since someone reviewed them. */
  changed: number;
  /** Open, and different from the official copy. */
  differs: number;
  severity: Exclude<Severity, "unknown"> | null;
  bySeverity: Record<Exclude<Severity, "unknown">, number>;
  /** Of what's worth a look: act, worth acting on; look, pattern matches only. Null: nothing open worth a look. */
  urgency: "act" | "look" | null;
  review: { by: Actor | null; at: string | null; people: number } | null;
  actions: ("review" | "reinstall")[];
  /** Other environments where these same open files were all reviewed (when every environment was asked for). */
  reviewedOn: { environmentId: string; label: string; by: Actor | null; at: string | null }[];
  /** Other environments where files reviewed here are open, byte for byte the same. */
  sameOpenOn: { environmentId: string; label: string; files: { id: string; sha256: string | null }[] }[];
};

export type ScanState = {
  last: {
    id?: string;
    at: string;
    status: "pending" | "running" | "succeeded" | "failed";
    trigger?: "manual" | "scheduled" | "after_remediation" | "daily";
    operationId?: string | null;
    checks?: ScanChecks;
    stats: ScanStats | null;
    /** Components with no published checksums, e.g. "plugin:acme-pro@2.1". */
    unverifiable: string[];
    rulesVersion: string | null;
    truncated?: boolean;
    error?: string | null;
  } | null;
  next: string | null;
  findings: ScanFinding[];
  /** The last scan that finished: what the findings stand on. Absent from an older API. */
  coverage?: {
    scanId: string;
    at: string;
    trigger: "manual" | "scheduled" | "after_remediation" | "daily";
    checks: ScanChecks;
    stats: ScanStats | null;
    truncated: boolean;
    unverifiable: string[];
    rulesVersion: string | null;
    /** The database check ran but couldn't read the database, in words. */
    databaseError?: string | null;
  } | null;
  /** By component, what needs attention first. Absent from an older API. */
  groups?: ScanGroup[];
  /** What the environment's agent can check beyond the base scan; false: it needs an agent update for it. Absent: an older API. */
  /** quarantineCheck: the site is checked without a quarantined file, which goes back if it broke. */
  can?: { database: boolean; javascript: boolean; daily: boolean; quarantineCheck?: boolean };
  /** The daily look at what was added or changed since the last scan. Null: the agent needs an update for it; absent: an older API. */
  daily?: {
    last: { at: string; status: "pending" | "running" | "succeeded" | "failed"; changed: number; found: number | null; error: string | null; operationId: string | null } | null;
    /** Null until the first full scan has run. */
    next: string | null;
  } | null;
};

export type UpdateItemStatus = "updated" | "rolled_back" | "skipped" | "failed";

export type UpdateResultItem = {
  type: "core" | "plugin" | "theme";
  slug: string;
  name?: string;
  from: string | null;
  to: string | null;
  status: UpdateItemStatus;
  /** Why it was rolled back, skipped or failed, in plain words. */
  reason: string | null;
};

export type UpdateRun = {
  id: string;
  trigger: "manual" | "auto";
  status: "pending" | "running" | "succeeded" | "partial" | "failed" | "cancelled" | "skipped";
  /** What was asked for. */
  items: { type: "core" | "plugin" | "theme"; slug: string; name?: string; from?: string | null; to?: string | null; toVersion?: string | null }[];
  /** Per item, once it has run. */
  results: UpdateResultItem[] | null;
  /** "Updated 4 of 5. WooCommerce 9.3.1 was rolled back (…), so it stays on 9.2.3." */
  summary: string | null;
  restorePoint?: { backupId: string } | null;
  operationId: string | null;
  actor: Actor | null;
  scheduledFor?: string | null;
  createdAt: string;
  finishedAt: string | null;
};

export type WafState = {
  mode: WafMode;
  last7Days: { detected: number; blocked: number };
  topRules: { id: string; message: string; count: number }[];
  samples: { at: string; ip: string; method: string; uri: string; ruleId: string; blocked: boolean }[];
};

export type PhpSupport = { version: string; branch?: string; activeUntil?: string | null; securityUntil: string | null; status: "supported" | "security-only" | "eol" | "unknown" };

export type EnvSecurity = {
  settings: SecuritySettings;
  inventory: Inventory | null;
  vulnerabilities: Vulnerability[];
  dataFreshness: { lastRefreshAt: string | null; stale: boolean };
  scan: ScanState;
  /** leaveAlone: what automatic updates leave alone here (absent from an older API). */
  updates: { autoUpdates: AutoUpdates; lastRun: UpdateRun | null; next: string | null; leaveAlone?: LeaveAlone };
  waf: WafState;
  phpSupport: PhpSupport | null;
};

export type SiteSecurityResponse = { environments: Record<string, EnvSecurity> };

/**
 * What automatic updates leave alone on an environment: plugins and themes held back (here, or on every environment of
 * the site), and versions that were rolled back here (tried again only once a newer one is out).
 */
export type LeaveAlone = {
  holds: { id: string; type: "plugin" | "theme"; slug: string; siteWide: boolean; at: string }[];
  skips: { type: "core" | "plugin" | "theme"; slug: string; version: string; reason: string | null; at: string }[];
};

export type SiteSecuritySummary = {
  vulnerabilities: { critical: number; high: number; medium: number; low: number };
  malware: number;
  /** How many of those are in WordPress's database; and what they're worth by the groups: act, or look (pattern matches only). Absent: an older API. */
  malwareDatabase?: number;
  malwareUrgency?: "act" | "look" | null;
  /** Open, and likely their plugin's or theme's own code: not in `malware`. */
  malwareOwnCode?: number;
  lastUpdateFailed: boolean;
};

export type LoginProtection = { enabled: boolean; maxRetry: number; findTimeMinutes: number; banMinutes: number };
export type ServerFirewall = { loginProtection: LoginProtection; allowIps: string[] };

/** Everything from the hourly audit is null until the first one has run. */
export type ServerSecurity = {
  /** Counts and list: what an install takes. */
  packages: {
    security: number;
    other: number;
    list: { name: string; from: string; to: string; security: boolean; origin: string }[];
    /** What it wouldn't: Ubuntu's phased updates, not yet released to this server, and what apt keeps back. Absent: an older API. */
    phased?: { name: string; from: string; to: string; security: boolean; origin: string }[];
    keptBack?: { name: string; from: string; to: string; security: boolean; origin: string }[];
  } | null;
  rebootRequired: boolean | null;
  rebootPackages: string[];
  unattended: { lastRunAt: string | null; ok: boolean | null } | null;
  bans: { count: number; list: { ip: string; jail: string; until: string | null }[] } | null;
  firewall: ServerFirewall;
  firewallAppliedAt?: string | null;
  auditedAt: string | null;
  /** When the package lists were last refreshed (the nightly apt update); "Other updates" are as of then. */
  aptUpdatedAt?: string | null;
  phpSupport: PhpSupport[];
  reboot?: ServerReboot | null;
  lastUpgrade?: {
    operationId: string;
    at: string;
    securityOnly: boolean;
    ok: boolean;
    upgraded: number;
    rebootRequired: boolean | null;
    error: string | null;
    /** Each ready environment's health, before and after. */
    checks: { environmentId: string; before: number | null; after: number | null; worse: boolean }[];
  } | null;
  /** Signs of a break-in from the hourly audit (docs/api.md, "Intrusion checks"). Absent: an older API. */
  intrusion?: ServerIntrusion;
};

export type ServerFindingKind = "tmp_executable" | "cron_entry" | "process" | "package_file" | "ssh_key";
export type ServerFinding = {
  id: string;
  kind: ServerFindingKind;
  group: ServerFindingKind;
  key: string;
  sha256: string | null;
  /** The Linux user it belongs to: a site's, root. */
  user: string | null;
  path: string | null;
  detail: string;
  severity: Exclude<Severity, "unknown">;
  status: "open" | "allowed";
  facts: { pid?: number; startedAt?: string; cpuPercent?: number; command?: string } | null;
  /** An SSH key there when the checks began (the baseline): low, and nothing raised. Absent: an older API. */
  known?: boolean;
  firstSeen: string;
  lastSeen: string;
  allowed: { by: Actor; at: string | null } | null;
  changedSinceReview: { by: Actor | null; at: string | null } | null;
};
export type ServerFindingGroup = {
  key: ServerFindingKind;
  kind: ServerFindingKind;
  /** "SSH keys", "Scheduled tasks", "Processes", "Temporary folders", "System files". */
  name: string;
  open: number;
  reviewed: number;
  changed: number;
  /** Open SSH keys from the baseline. Absent: an older API. */
  known?: number;
  severity: Exclude<Severity, "unknown"> | null;
  bySeverity: Record<Exclude<Severity, "unknown">, number>;
  /** act: something open is high or critical. */
  urgency: "act" | "look" | null;
  review: { by: Actor; at: string | null; people: number } | null;
  actions: "review"[];
};
export type ServerIntrusion = {
  /** False: the agent needs an update for these checks. */
  supported: boolean;
  checkedAt: string | null;
  /** What the last audit with them ran. */
  checks: { tmp: boolean; cron: boolean; processes: boolean; packages: boolean; sshKeys: boolean } | null;
  /** The last dpkg --verify (about once a day). */
  packages: { checkedAt: string; list: string[] } | null;
  findings: ServerFinding[];
  groups: ServerFindingGroup[];
};
